import { Http } from '@gamiphy/http';
import {
  AppMission, Category, DraftAppMission, DraftCategory, UserMission, UserMissionCategoryPopulated,
} from '@gamiphy/bl/mission';

export class MissionsApi {
  public static async getUserMissions(userId: string): Promise<UserMissionCategoryPopulated[]> {
    const response = await Http.get<GetUserMissionsPopulatedCategoryResponse>({
      url: '/v2/user-missions/standing',
      query: {
        user: userId,
      },
    });

    return response.data.userMissions;
  }

  public static async createCategory(draftCategory: DraftCategory): Promise<Category> {
    const response = await Http.post<CreateCategoryResponse>({
      url: '/v2/missions-categories',
      payload: draftCategory,
    });

    return response.data.missionCategory;
  }

  public static async getCategories(): Promise<Category[] | []> {
    const response = await Http.get<GetCategoriesResponse>({
      url: '/v2/missions-categories',
      query: {
        limit: 100,
      },
    });

    return response.data.missionsCategories;
  }

  public static async getAppMissions(): Promise<AppMission[] | []> {
    const response = await Http.get<GetAppMissionsResponse>({
      url: '/v2/missions',
      query: {
        limit: 100,
      },
    });

    return response.data.missions;
  }

  public static async updateMission(id: string, data: Partial<AppMission>): Promise<AppMission> {
    const response = await Http.patch<UpdateMissionsResponse>({
      url: `/v2/missions/${id}`,
      payload: data,
    });

    return response.data.mission;
  }

  public static async updateMissionCategory(id: string, data: Partial<Category>): Promise<Category> {
    const response = await Http.put<UpdateCategoryResponse>({
      url: `/v2/missions-categories/${id}`,
      payload: data,
    });

    return response.data.missionCategory;
  }

  public static async deleteMission(id: string): Promise<void> {
    await Http.delete({
      url: `/v2/missions/${id}`,
    });
  }

  public static async createMission(data: DraftAppMission & { category: string }): Promise<AppMission> {
    const response = await Http.post<CreateMissionsResponse>({
      url: '/v2/missions/',
      payload: data,
    });

    return response.data.mission;
  }

  public static async bulkCreateMissions(data: DraftAppMission[]): Promise<AppMission[]> {
    const response = await Http.post<BulkCreateMissionsResponse>({
      url: '/v2/missions/bulk',
      payload: data,
    });

    return response.data.missions;
  }

  public static async sortMissions(missionIds: string[]): Promise<AppMission[]> {
    const response = await Http.put<GetAppMissionsResponse>({
      url: '/v2/missions/sort',
      payload: {
        missions: missionIds,
      },
    });

    return response.data.missions;
  }

  public static async sortMissionCategories(missionCategoryIds: {
    missionsCategories: string[]
  }): Promise<Category[] | []> {
    const response = await Http.put<GetCategoriesResponse>({
      url: '/v2/missions-categories/sort',
      payload: missionCategoryIds,
    });

    return response.data.missionsCategories;
  }

  public static async getCompletedUserMissions(userId: string): Promise<UserMission[]> {
    const response = await Http.get<GetUserMissionsResponse>({
      url: '/v2/user-missions',
      query: {
        user: userId,
        status: 'completed',
        populate: ['mission'],
        limit: 100,
      },
    });

    return response.data.userMissions;
  }
}

export type GetUserMissionsResponse = {
  userMissions: UserMission[]
}
export type GetUserMissionsPopulatedCategoryResponse = {
  userMissions: UserMissionCategoryPopulated[]
}
export type CreateCategoryResponse = {
  missionCategory: Category
}
export type GetCategoriesResponse = {
  missionsCategories: Category[] | []
}
export type GetAppMissionsResponse = {
  missions: AppMission[] | []
}
export type CreateMissionsResponse = {
  mission: AppMission
}
export type UpdateMissionsResponse = {
  mission: AppMission
}
export type UpdateCategoryResponse = {
  missionCategory: Category
}
export type BulkCreateMissionsResponse = {
  missions: AppMission[]
}
